import React from 'react';
import TrackingDemoSection from './TrackingDemoSection';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';

const HeroSection = ({ onGuestAction }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  return (
    <>
      {/* Hero — asymmetric, premium */}
      <section className="relative bg-[#0d1f35] overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        {/* Ambient glow */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10" style={{background: 'radial-gradient(circle, #ff8c00 0%, transparent 70%)', transform: 'translate(30%, -30%)'}} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-8" style={{background: 'radial-gradient(circle, #1e4a72 0%, transparent 70%)', transform: 'translate(-30%, 30%)'}} />

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl py-14 sm:py-20">
          <div className="grid lg:grid-cols-[1fr_380px] gap-16 items-center">
            {/* Left — headline */}
            <div>
              {/* Live badge */}
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm mb-8">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                <span className="text-sm font-medium text-emerald-300 tracking-wide">{t('hero.liveBadge', 'Live Marketplace · Sugar Available Now')}</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-[58px] font-black text-white leading-[1.08] tracking-tight mb-6">
                {t('hero.headline', "India's 1st Organised Sugar Economy")}
              </h1>

              {/* Sub-line */}
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
                {t('hero.description', 'Competitive prices uploaded by mills • Verified trucks • Secure transparent payments')}
              </p>
              <p className="text-lg sm:text-xl font-black text-[#ff8c00] mb-10 tracking-wide">{t('hero.tagline', 'Book it. Track it. Receive it.')}</p>

              {/* Primary Market Access */}
              <p className="text-xs font-bold text-[#ff8c00] uppercase tracking-[0.2em] mb-3">
                {t('hero.primaryMarketAccess', 'Primary Market Access')}
              </p>
              {/* Live Tender Info button intentionally removed (temporarily, pending
                  legal/compliance review of the scraped source data) — see
                  src/pages/live-tender-info/ and src/Routes.jsx for the restore
                  point. This was previously a 2-up grid with the Mandi button;
                  now a single full-width button so the layout stays balanced
                  without a placeholder in the second slot. */}
              <div className="mb-5">
                <button
                  onClick={() => navigate('/mandi')}
                  className="flex items-start gap-4 text-left p-5 rounded-2xl bg-[#ff8c00] hover:bg-[#f07d00] shadow-lg hover:shadow-[#ff8c00]/40 hover:shadow-xl transition-all duration-200"
                >
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="Store" size={22} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white text-base font-bold leading-tight">{t('hero.mandiTitle', 'SugarHathi Mandi')}</p>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-white/20 rounded-full px-2 py-0.5">{t('hero.mandiSubtitle', 'Buy Sugar')}</span>
                    </div>
                    <p className="text-white/80 text-xs mt-1 leading-snug">{t('hero.mandiDescription', 'Live rates, available sugar and direct buying.')}</p>
                  </div>
                </button>
              </div>

              {/* Secondary CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <button
                  onClick={() => navigate('/sell-your-sugar')}
                  className="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-[#ff8c00] hover:bg-[#f07d00] text-white text-base font-bold rounded-xl shadow-lg hover:shadow-[#ff8c00]/40 hover:shadow-xl transition-all duration-200"
                >
                  <Icon name="Tag" size={20} />
                  {t('hero.sellYourSugar', 'Sell Your Sugar')}
                </button>
                <button
                  onClick={() => navigate('/market-intelligence')}
                  className="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-base font-bold rounded-xl shadow-lg hover:shadow-[#3B82F6]/40 hover:shadow-xl transition-all duration-200"
                >
                  <Icon name="TrendingUp" size={20} />
                  {t('hero.govtQuotaTracker', 'Govt Sugar Quota Tracker')}
                </button>
                <button
                  onClick={() => navigate('/login-authentication')}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white/20 text-white/80 text-base font-medium rounded-xl hover:bg-white/8 hover:text-white transition-all duration-200"
                >
                  {t('hero.signIn', 'Sign In')}
                  <Icon name="ArrowRight" size={16} />
                </button>
              </div>
            </div>

            {/* Right — stats card */}
            <div className="hidden lg:block">
              <div className="rounded-2xl border border-white/20 bg-white/8 backdrop-blur-sm p-6">
                <p className="text-xs font-semibold text-slate-300 uppercase tracking-widest mb-5">{t('hero.platformAtGlance', 'Platform at a Glance')}</p>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { icon: 'Factory', value: '50+', label: t('hero.statVerifiedMills', 'Verified Mills'), color: 'text-[#ff8c00]', bg: 'bg-[#ff8c00]/15', border: 'border-[#ff8c00]/30' },
                    { icon: 'Truck', value: '600+', label: t('hero.statTransporters', 'Transporters'), color: 'text-sky-300', bg: 'bg-sky-400/15', border: 'border-sky-400/30' },
                    { icon: 'IndianRupee', value: '₹500Cr+', label: t('hero.statMovedSafely', 'Moved Safely'), color: 'text-emerald-300', bg: 'bg-emerald-400/15', border: 'border-emerald-400/30' },
                    { icon: 'ShieldCheck', value: '100%', label: t('hero.statSecurePayments', 'Secure Payments'), color: 'text-violet-300', bg: 'bg-violet-400/15', border: 'border-violet-400/30' },
                  ]?.map((item, i) => (
                    <div key={i} className={`flex flex-col items-center justify-center p-4 rounded-xl ${item?.bg} border ${item?.border}`}>
                      <Icon name={item?.icon} size={20} className={`${item?.color} mb-2`} />
                      <p className={`font-black text-xl leading-tight ${item?.color}`}>{item?.value}</p>
                      <p className="text-white/80 text-xs mt-0.5 text-center font-medium">{item?.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-white/15">
                  <Icon name="Clock" size={14} className="text-slate-400" />
                  <span className="text-xs text-slate-400">{t('hero.supportTeam')} <span className="text-white font-semibold">{t('hero.supportAvailable', 'Available')}</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Trust strip */}
      <section className="bg-[#ff8c00] py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-2">
            {[
                t('hero.trustVerifiedMills', '✓ 50+ Verified Mills'),
                t('hero.trustTransporters', '✓ 600+ Active Transporters'),
                t('hero.trustMovedSafely', '✓ ₹500Cr+ Moved Safely'),
                t('hero.trustGuaranteedDelivery', '✓ Guaranteed Delivery'),
              ]?.map((item, i) => (
              <span key={i} className="text-white text-sm font-semibold tracking-wide">{item}</span>
            ))}
          </div>
        </div>
      </section>
      <TrackingDemoSection />
      {/* How it works — bento asymmetric */}
      <section className="bg-[#f8f7f4] py-20 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="mb-14">
            <p className="text-xs font-bold text-[#ff8c00] uppercase tracking-[0.2em] mb-3">How it works — Buyer Flow</p>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0d1f35] leading-tight">
              Browse. Book. Track.<br />
              <span className="text-slate-400 font-normal">Three steps. Zero friction.</span>
            </h2>
          </div>

          {/* Bento grid — varied sizes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Step 1 — large */}
            <div className="lg:col-span-2 bg-[#0d1f35] rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10" style={{background: 'radial-gradient(circle, #ff8c00 0%, transparent 70%)', transform: 'translate(30%, -30%)'}} />
              <span className="text-7xl font-black text-white/8 absolute top-4 right-6 select-none">01</span>
              <div className="w-12 h-12 rounded-xl bg-[#ff8c00]/15 flex items-center justify-center mb-5">
                <Icon name="Search" size={22} className="text-[#ff8c00]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Browse mill listings with live prices & quantities</h3>
              <p className="text-slate-400 leading-relaxed max-w-sm">
                Filter by state, grade, and mill. Every listing shows live price per ton, available quantity, and factory location. All mills are KYC-verified — no middlemen, no private godowns.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {['M-Grade', 'S-Grade', 'L-Grade', 'Raw Sugar']?.map(g => (
                  <span key={g} className="px-3 py-1 rounded-full bg-white/8 text-white/60 text-xs font-medium">{g}</span>
                ))}
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/20">
                <Icon name="BadgeCheck" size={13} className="text-emerald-400" />
                <span className="text-xs font-semibold text-emerald-400">Verified Badge on every mill</span>
              </div>
            </div>

            {/* Step 2 — tall */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm relative overflow-hidden">
              <span className="text-7xl font-black text-slate-100 absolute top-4 right-6 select-none">02</span>
              <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center mb-5">
                <Icon name="Truck" size={22} className="text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-[#0d1f35] mb-3">Book sugar + transport together</h3>
              <p className="text-slate-500 leading-relaxed text-sm">
                One-click SmartLoad booking bundles your sugar order with a verified transporter. No separate coordination. No phone calls. Single invoice.
              </p>
              <div className="mt-6 p-3 rounded-xl bg-sky-50 border border-sky-100">
                <p className="text-xs font-semibold text-sky-700">SmartLoad Bundle</p>
                <p className="text-xs text-sky-600 mt-1">Sugar + Verified Truck · Single booking · One invoice</p>
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-100">
                <Icon name="BadgeCheck" size={13} className="text-sky-600" />
                <span className="text-xs font-semibold text-sky-600">Verified Transporters only</span>
              </div>
            </div>

            {/* Step 3 — wide */}
            <div className="md:col-span-2 lg:col-span-1 bg-emerald-950 rounded-2xl p-8 relative overflow-hidden">
              <span className="text-7xl font-black text-white/8 absolute top-4 right-6 select-none">03</span>
              <div className="w-12 h-12 rounded-xl bg-emerald-400/15 flex items-center justify-center mb-5">
                <Icon name="ShieldCheck" size={22} className="text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Track live + secure payment on delivery</h3>
              <p className="text-emerald-200/70 leading-relaxed text-sm">
                Follow your shipment in real time. Payment on delivery confirmation — funds are released only after you confirm receipt. Full operations team support throughout.
              </p>
              <div className="mt-5 space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="Lock" size={14} className="text-emerald-400" />
                  <span className="text-xs text-emerald-400 font-medium">100% Secure, Documented Transactions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={14} className="text-emerald-400" />
                  <span className="text-xs text-emerald-400 font-medium">Live GPS tracking included</span>
                </div>
              </div>
            </div>

            {/* Fallback note */}
            <div className="md:col-span-2 lg:col-span-2 bg-amber-50 border border-amber-100 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Headphones" size={18} className="text-amber-700" />
              </div>
              <div>
                <p className="font-bold text-amber-900 mb-1">Delayed delivery? Our team steps in within 4 hours.</p>
                <p className="text-amber-700 text-sm leading-relaxed">
                  If your delivery is delayed or disputed, our operations team intervenes within 4 hours. Payments are held securely until the issue is fully resolved. You're never left without recourse.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 p-6 rounded-2xl bg-[#0d1f35]">
            <div>
              <p className="text-white font-bold text-lg">Ready to place your first order?</p>
              <p className="text-slate-400 text-sm mt-1">Join 50+ verified mills and 600+ transporters already on the platform.</p>
            </div>
            <button
              onClick={() => navigate('/sugar-marketplace-dashboard')}
              className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-[#ff8c00] hover:bg-[#f07d00] text-white font-bold rounded-xl transition-all duration-200 shadow-lg hover:shadow-[#ff8c00]/30"
            >
              Browse Sugar Offers
              <Icon name="ArrowRight" size={18} />
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;